/**
 * Le contrat avec l'API, vu du client : un type par document, un appel par route.
 *
 * C'est le **premier fichier à lire** pour savoir ce que le serveur sait faire. Il est rangé
 * par ressource, dans le même ordre que `apps/api/src/routes/` — si un appel manque ici, la
 * route n'est pas utilisée par l'interface.
 */
/* === Santé et session ======================================================================== */
export interface User {
  email: string;
  name: string;
  picture: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    super(code);
  }
}

/** Utilisateur courant (+ rôle), ou null si non connecté. */
/**
 * Qui est connecté, et ce qu'il a le droit de faire.
 *
 * Trois niveaux : tout membre **voit** tout (y compris les photos à trier), un **éditeur**
 * range (créer un album, y ajouter ou en retirer des photos), le **propriétaire** détruit et
 * administre. Le propriétaire est toujours éditeur.
 */
export async function fetchMe(): Promise<{
  user: User;
  isOwner: boolean;
  isEditor: boolean;
} | null> {
  const res = await fetch("/api/auth/me");
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`me failed: ${res.status}`);
  return (await res.json()) as { user: User; isOwner: boolean; isEditor: boolean };
}

/** Échange un ID token Google contre une session. Renvoie l'utilisateur. */
export async function createSession(credential: string): Promise<User> {
  const res = await fetch("/api/auth/session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ credential }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error ?? "error");
  }
  return ((await res.json()) as { user: User }).user;
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST" });
}

/* === Albums ================================================================================== */
/* L'appartenance des photos vit dans l'album (DESIGN §3.2) : ajouter ne retire de nulle part. */
export interface EventSummary {
  id: string;
  title: string;
  dateApprox: string | null;
  dateYear: number | null;
  dateApproxFlag: boolean;
  /** Déduit côté serveur de la liste des photos — ne peut plus dériver. */
  photoCount: number;
  coverPhotoId: string | null;
  description?: string | null;
  descriptionBy?: string | null; // "Diapo" si rédigée par l'assistant
  /** Déduit du `createTime` Firestore quand le champ manque (albums d'avant le 29/07/2026). */
  createdAt?: string | null;
}

export type EventField = "title" | "dateApprox" | "description";

export interface EventEdit {
  id: string;
  eventId: string;
  field: EventField;
  from: string | null;
  to: string | null;
  author: string;
  authorName: string;
  at: string;
}

/** Ce que coûte la suppression d'un album, à afficher **avant** de confirmer. */
export interface EventImpact {
  photoCount: number;
  commentCount: number;
  /** Photos qui retourneraient dans « À trier » — les autres sont rangées ailleurs. */
  becomesLoose: number;
}

export async function fetchEvents(): Promise<{ events: EventSummary[]; looseCount: number }> {
  const res = await fetch("/api/events");
  if (!res.ok) throw new Error(`events failed: ${res.status}`);
  return (await res.json()) as { events: EventSummary[]; looseCount: number };
}

export async function fetchEvent(
  id: string,
): Promise<{ event: EventSummary; photos: Photo[]; comments: Comment[] }> {
  const res = await fetch(`/api/events/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`event failed: ${res.status}`);
  return (await res.json()) as { event: EventSummary; photos: Photo[]; comments: Comment[] };
}

/** Titre vide = brouillon : on groupe maintenant, on nommera plus tard. */
export async function createEventFromPhotos(
  title: string,
  dateApprox: string | null,
  photoIds: string[],
): Promise<EventSummary> {
  const res = await fetch("/api/events/from-photos", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ title, dateApprox, photoIds }),
  });
  if (!res.ok) throw new Error(`create event failed: ${res.status}`);
  return ((await res.json()) as { event: EventSummary }).event;
}

export async function updateEvent(
  id: string,
  patch: { title?: string; dateApprox?: string; description?: string },
): Promise<EventSummary> {
  const res = await fetch(`/api/events/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`update event failed: ${res.status}`);
  return ((await res.json()) as { event: EventSummary }).event;
}

/** Supprime l'album. Les photos survivent : autres albums, ou « À trier ». */
export async function deleteEvent(id: string): Promise<void> {
  const res = await fetch(`/api/events/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`delete event failed: ${res.status}`);
}

export async function fetchEventImpact(id: string): Promise<EventImpact> {
  const res = await fetch(`/api/events/${encodeURIComponent(id)}/impact`);
  if (!res.ok) throw new Error(`impact failed: ${res.status}`);
  return (await res.json()) as EventImpact;
}

export async function fetchEventHistory(id: string): Promise<EventEdit[]> {
  const res = await fetch(`/api/events/${encodeURIComponent(id)}/history`);
  if (!res.ok) throw new Error(`history failed: ${res.status}`);
  return ((await res.json()) as { edits: EventEdit[] }).edits;
}

/** Fait rédiger la description de l'album par Diapo, à partir des commentaires. */
export async function describeEvent(id: string): Promise<EventSummary> {
  const res = await fetch(`/api/events/${encodeURIComponent(id)}/describe`, { method: "POST" });
  if (!res.ok) throw new Error(`describe failed: ${res.status}`);
  return ((await res.json()) as { event: EventSummary }).event;
}

export async function postEventComment(id: string, text: string): Promise<Comment> {
  const res = await fetch(`/api/events/${encodeURIComponent(id)}/comment`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(`comment failed: ${res.status}`);
  return ((await res.json()) as { comment: Comment }).comment;
}

export async function addPhotosToEvent(eventId: string, photoIds: string[]): Promise<void> {
  const res = await fetch(`/api/events/${encodeURIComponent(eventId)}/add-photos`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ photoIds }),
  });
  if (!res.ok) throw new Error(`add photos failed: ${res.status}`);
}

/**
 * Retire des photos **d'un album donné**. Une photo appartenant à plusieurs albums reste
 * dans les autres ; elle ne revient dans « À trier » que si c'était le dernier.
 */
export async function removePhotosFromEvent(
  eventId: string,
  photoIds: string[],
): Promise<{ removed: number; dissolved: string[] }> {
  const res = await fetch(`/api/events/${encodeURIComponent(eventId)}/remove-photos`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ photoIds }),
  });
  if (!res.ok) throw new Error(`remove failed: ${res.status}`);
  return (await res.json()) as { removed: number; dissolved: string[] };
}

/* === Photos ================================================================================== */
/* Le bucket est privé : les pixels ne passent que par ces URLs, session obligatoire. */
export interface Photo {
  id: string;
  scanName: string;
  width: number;
  height: number;
  flickrDescription: string;
  flickrDateTaken: string | null;
}

export async function fetchPhoto(
  id: string,
): Promise<{ photo: Photo & { note?: string }; comments: Comment[]; albums: EventSummary[] }> {
  const res = await fetch(`/api/photos/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`photo failed: ${res.status}`);
  return (await res.json()) as {
    photo: Photo & { note?: string };
    comments: Comment[];
    albums: EventSummary[];
  };
}

export async function postPhotoComment(id: string, text: string): Promise<Comment> {
  const res = await fetch(`/api/photos/${encodeURIComponent(id)}/comment`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(`comment failed: ${res.status}`);
  return ((await res.json()) as { comment: Comment }).comment;
}

export const thumbUrl = (id: string): string => `/api/photos/${encodeURIComponent(id)}/thumb`;

export const fullUrl = (id: string): string => `/api/photos/${encodeURIComponent(id)}/full`;

/* === À trier ================================================================================= */
/* Les photos qui ne sont dans aucun album, et leurs pré-groupes. */
export interface LooseCluster {
  photoIds: string[];
  scanNames: string[];
  sheetFrom: number;
  sheetTo: number;
  /** Lot final des photos sans voisin ressemblant (mode similarité). */
  singles?: boolean;
}

/** « planche » = séries de scan contiguës ; « similarité » = photos presque identiques. */
export type ClusterMode = "sheet" | "similar";

export interface LooseClusters {
  clusters: LooseCluster[];
  total: number;
  mode: ClusterMode;
  /** Photos réellement regroupées (hors lot des isolées). */
  grouped: number;
  /** Photos dont l'empreinte de contenu est calculée — distingue « rien ne se ressemble »
   *  de « les empreintes manquent ». */
  embedded: number;
}

export async function fetchClusters(mode: ClusterMode = "sheet"): Promise<LooseClusters> {
  const res = await fetch(`/api/loose/clusters?mode=${mode}`);
  if (!res.ok) throw new Error(`clusters failed: ${res.status}`);
  return (await res.json()) as LooseClusters;
}

/* === Commentaires ============================================================================ */
/* Même geste sur un album et sur une photo. `speakerName` n'est jamais deviné. */
export interface Comment {
  id: string;
  eventId?: string | null;
  photoId?: string | null;
  author: string;
  authorName: string;
  source: "voice" | "written" | "flickr";
  text: string;
  recordingId?: string | null;
  tStartMs?: number;
  tEndMs?: number;
  /** `onsetMs` = début de la parole dans le segment. L'audio n'est pas modifié. */
  segments?: {
    seq: number;
    startMs: number;
    onsetMs?: number | null;
    durationMs?: number | null;
  }[];
  /** Voix qui parle, une fois nommée par la famille. Jamais devinée. */
  speakerLabel?: number | null;
  speakerName?: string | null;
  originalText?: string;
  editedBy?: string;
  editedAt?: string;
  createdAt: string;
}

/** Nombre de segments audio réécoutables d'un commentaire vocal. */
export function audioParts(c: Comment): number {
  if (!c.recordingId) return 0;
  return c.segments?.length ?? 1; // avant l'ajout de `segments` : un seul segment
}

export const commentAudioUrl = (id: string, index: number): string =>
  `/api/comments/${encodeURIComponent(id)}/audio/${index}`;

/** Corrige le texte (noms mal entendus). La transcription d'origine est conservée. */
export async function patchComment(id: string, text: string): Promise<Comment> {
  const res = await fetch(`/api/comments/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(`patch comment failed: ${res.status}`);
  return ((await res.json()) as { comment: Comment }).comment;
}

export async function deleteComment(id: string): Promise<void> {
  const res = await fetch(`/api/comments/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`delete comment failed: ${res.status}`);
}

/* === Sessions d'enregistrement et voix ======================================================= */
/* Un `label` de voix ne vaut que dans sa session ; le registre `voices` porte le nom durable. */
export interface TimelineEntry {
  tMs: number;
  eventId: string | null;
  photoId?: string | null;
}

/** Une voix repérée dans une session. `label` ne vaut que pour cette session. */
export interface SessionSpeaker {
  label: number;
  voice: string;
  sampleSeq?: number | null;
  voiceId?: string | null;
  name?: string | null;
}

export interface Recording {
  id: string;
  author: string;
  startedAt: string;
  durationMs: number;
  status: string;
  timeline: { tMs: number; eventId: string | null }[];
  speakers?: SessionSpeaker[];
  speakerCount?: number;
}

/** Une voix connue de la famille, réutilisable d'une session à l'autre. */
export interface Voice {
  id: string;
  name: string;
  sessions: number;
}

/** Un passage du journal : ce qui a été dit, et ce qu'on regardait à ce moment-là. */
export interface Passage {
  id: string;
  tStartMs: number;
  text: string;
  speakerLabel: number | null;
  speakerName: string | null;
  authorName: string;
  segments: number;
  /** Contexte, pas sujet : ce qui était à l'écran quand la phrase a été dite. */
  photoId: string | null;
  eventId: string | null;
  eventTitle: string | null;
}

export interface Journal {
  recording: { id: string; startedAt: string; durationMs: number; speakers: SessionSpeaker[] };
  passages: Passage[];
  /** Passages sans aucun contexte — ils existent quand même, c'est tout l'objet du journal. */
  sansContexte: number;
}

/**
 * La conversation entière d'une session, dans l'ordre.
 *
 * Nécessaire parce que regarder une photo déclenche des souvenirs qui ne parlent pas d'elle :
 * les rattacher de force serait une interprétation, ne pas les rattacher les ferait
 * disparaître.
 */
export async function fetchJournal(recordingId: string): Promise<Journal> {
  const res = await fetch(`/api/recordings/${encodeURIComponent(recordingId)}/journal`);
  if (!res.ok) throw new Error(`journal failed: ${res.status}`);
  return (await res.json()) as Journal;
}

export async function fetchRecordings(): Promise<Recording[]> {
  const res = await fetch("/api/recordings");
  if (!res.ok) throw new Error(`recordings failed: ${res.status}`);
  return ((await res.json()) as { recordings: Recording[] }).recordings;
}

export async function transcribeRecordingById(id: string): Promise<void> {
  const res = await fetch(`/api/recordings/${encodeURIComponent(id)}/transcribe`, { method: "POST" });
  if (!res.ok) throw new Error(`transcribe failed: ${res.status}`);
}

export async function deleteRecording(id: string): Promise<number> {
  const res = await fetch(`/api/recordings/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`delete recording failed: ${res.status}`);
  return ((await res.json()) as { deletedComments: number }).deletedComments;
}

export async function fetchVoices(): Promise<Voice[]> {
  const res = await fetch("/api/voices");
  if (!res.ok) throw new Error(`voices failed: ${res.status}`);
  return ((await res.json()) as { voices: Voice[] }).voices;
}

export const speakerAudioUrl = (recordingId: string, label: number): string =>
  `/api/recordings/${encodeURIComponent(recordingId)}/speakers/${label}/audio`;

/** Nomme les voix d'une session. Un nom vide retire l'attribution. */
export async function nameSpeakers(
  recordingId: string,
  names: { label: number; name: string }[],
): Promise<SessionSpeaker[]> {
  const res = await fetch(`/api/recordings/${encodeURIComponent(recordingId)}/speakers`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ names }),
  });
  if (!res.ok) throw new Error(`name speakers failed: ${res.status}`);
  return ((await res.json()) as { speakers: SessionSpeaker[] }).speakers;
}

/* === Lexique ================================================================================= */
/* Les noms propres appris des corrections, exposés pour être corrigibles. */
export interface LexiconEntry {
  id: string;
  name: string;
  heard: string[];
  count: number;
  updatedAt: string;
}

/** Noms retenus par les corrections, injectés dans les transcriptions suivantes. */
export async function fetchLexicon(): Promise<LexiconEntry[]> {
  const res = await fetch("/api/lexicon");
  if (!res.ok) throw new Error(`lexicon failed: ${res.status}`);
  return ((await res.json()) as { entries: LexiconEntry[] }).entries;
}

export async function deleteLexiconEntry(id: string): Promise<void> {
  const res = await fetch(`/api/lexicon/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`delete lexicon failed: ${res.status}`);
}

/* === Accès ================================================================================== */
/**
 * Qui peut entrer, et à quel titre. Propriétaire uniquement.
 *
 * Le propriétaire n'apparaît pas dans cette liste : son rôle vient de la configuration du
 * serveur, justement pour qu'aucune manipulation ici ne puisse le déposséder.
 */
export interface Access {
  id: string;
  email: string;
  role: "member" | "editor";
  addedBy: string;
  addedAt: string;
}

export async function fetchAccess(): Promise<{ access: Access[]; owner: string }> {
  const res = await fetch("/api/access");
  if (!res.ok) throw new ApiError(res.status, "access failed");
  return (await res.json()) as { access: Access[]; owner: string };
}

export async function setAccess(email: string, role: Access["role"]): Promise<Access> {
  const res = await fetch(`/api/access/${encodeURIComponent(email)}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    throw new ApiError(res.status, ((await res.json()) as { error?: string }).error ?? "erreur");
  }
  return ((await res.json()) as { access: Access }).access;
}

export async function removeAccess(email: string): Promise<void> {
  const res = await fetch(`/api/access/${encodeURIComponent(email)}`, { method: "DELETE" });
  if (!res.ok) throw new ApiError(res.status, "remove access failed");
}
